import { type FC } from "react";
import { Loader } from "semantic-ui-react";
import { LicenseTypes } from "enums/licensing/licenseType";
import { type AccountPackLicense } from "features/Accounts/types";
import { type PackLicenseType } from "features/Licensing/Packs/types/state";
import CardThumbnail from "components/cards/cardThumbnail/CardThumbnail";
import LicensePicker from "components/LicensePicker/LicensePicker";
import { type ModalWithStepsStep } from "components/modal/ModalWithSteps";
import { MAX_LICENSE_COUNT } from "features/Accounts/PackAssignmentModal/constants";
import { LicenseCountInput } from "./LicenseCountInput/LicenseCountInput";

import styles from "./LicensesStep.module.scss";
import FieldLabel from "components/forms/FieldLabel";

export interface LicenseEditingStepPropsOwn extends ModalWithStepsStep {
  packId: number;
  isLoading: boolean;
  error?: Error;
  accountPackLicense?: AccountPackLicense;
  licenseTypes: PackLicenseType[];
  onLicenseCountChange: (licensesCount?: number) => void;
  onExpirationDateChange: (optionId?: number, date?: Date | null) => void;
}

const LicenseEditingStep: FC<LicenseEditingStepPropsOwn> = ({
  packId,
  isLoading,
  error,
  accountPackLicense,
  licenseTypes,
  onLicenseCountChange,
  onExpirationDateChange,
}) => {
  const getLicenseOptions = (options: PackLicenseType[]) => {
    return options
      .filter((lt) => lt.id !== LicenseTypes.Trial)
      .map((lt) => {
        return { id: lt.id, title: lt.name };
      });
  };

  return (
    <div className={styles.container}>
      {!isLoading && (
        <div className={styles.item}>
          <div className={styles.image}>
            <CardThumbnail thumbnailUrl={accountPackLicense?.thumbnailImageUrl}>
              <div className={styles.title}>{accountPackLicense?.packTitle}</div>
            </CardThumbnail>
          </div>

          <div className={styles.fields}>
            <div className={styles.field}>
              <LicenseCountInput
                onLicenseCountChange={(_packId: number, licensesCount?: number) => {
                  onLicenseCountChange(licensesCount);
                }}
                packId={packId}
                licensesCount={accountPackLicense?.totalLicensesCount}
                assingedLicensesCount={accountPackLicense?.assingedLicensesCount}
                maxLicenseCount={MAX_LICENSE_COUNT}
                unlimitedLicenses={accountPackLicense?.unlimitedLicenses}
                label={<FieldLabel label={"Licenses"} className={styles.label} markAsRequired />}
              />
            </div>
            <LicensePicker
              options={getLicenseOptions(licenseTypes)}
              onOptionChange={(optionId?: number) => {
                onExpirationDateChange(optionId);
              }}
              onExpirationDateChange={(date?: null | Date) => {
                onExpirationDateChange(undefined, date);
              }}
              preselectedOptionId={accountPackLicense?.licenseTypeId}
              preselectedExpirationDate={accountPackLicense?.expirationDate}
              verticalPosition="bottom"
              className={styles.field}
              noExpiration={accountPackLicense?.noExpiration}
              labelClassName={styles.label}
            />
          </div>
        </div>
      )}
      <Loader active={isLoading} />
      {error && (
        <div className={styles.error} data-testid="error-info">
          Error loading data!
        </div>
      )}
    </div>
  );
};

export default LicenseEditingStep;
